// "What we know" about one outbound message — a PURE reading of the evidence
// on a delivery row (2026-09-03). No inference beyond what the columns say:
//
//   failedAt / failReason      the trace's terminal failure — their server
//                              refused it (SMTP 5xx) or it expired
//   failReason, no failedAt    Exchange Online HELD it (quarantined /
//                              filtered as spam): terminal, no moment named
//   acceptedAt / acceptedBy    the remote MX took it (SMTP 250): "their
//                              server has it", NOT "a person read it"
//   gateway                    what kind of server accepted it
//   firstMachineClickAt        a scanner fetched the tracked link at/after
//                              this send — proves arrival at their security
//                              layer, says nothing about a person
//   firstHumanClickAt/VisitAt  graded human engagement (migration 017) —
//                              INVITE-level, so an earlier wave's open shows
//                              on a later wave's row too
//   rsvp / rsvpAt              the person answered
//
// Rules are ordered strongest-fact-first. A row can carry several true facts
// (a bounced reminder to someone who RSVP'd yes off wave 1) and the verdict
// names both rather than hiding one.

import type { DeliveryRow } from './delivery-types'

export type VerdictTone = 'ok' | 'warn' | 'alert' | 'neutral' | 'info'

export interface Verdict {
  /** Stable key for tests, filters and CSS. */
  key: 'failed' | 'held' | 'rsvp' | 'visited' | 'opened_no_visit' | 'scanned_only' | 'accepted_silent'
    | 'in_transit' | 'not_traced' | 'pre_tracing'
  text: string
  tone: VerdictTone
}

/** Exchange Online kept the message: the sweep writes fail_reason with NO
 *  failed_at (no hop names a moment). Terminal — it did not arrive. Same
 *  rule as the API's isHeld in routes/symposium-delivery.ts. */
export const HELD_STATUSES: readonly string[] = ['quarantined', 'filteredasspam']
export function isHeld(r: Pick<DeliveryRow, 'acceptedAt' | 'failReason' | 'traceStatus'>): boolean {
  if (r.acceptedAt) return false
  return r.failReason != null || HELD_STATUSES.includes(r.traceStatus ?? '')
}

export type VerdictInput = Pick<DeliveryRow,
  'sentAt' | 'traceStatus' | 'acceptedAt' | 'acceptedBy' | 'gateway' | 'failedAt' | 'failReason'
  | 'firstMachineClickAt' | 'firstHumanClickAt' | 'firstHumanVisitAt' | 'rsvp' | 'rsvpAt' | 'rsvpSource'>

/** First sends the sweep can see: symposium_sends + the Message-ID header
 *  landed 2026-09-03; the trace API looks back 90 days at most, so anything
 *  older than this is not "untraced", it predates tracing. */
export const TRACING_SINCE = '2026-09-01T00:00:00Z'

const ANSWERED = new Set(['yes', 'no', 'maybe'])

function fmtDay(iso: string | null): string {
  if (!iso) return '(date not recorded)'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function acceptedByLabel(r: VerdictInput): string {
  if (r.gateway && r.gateway !== 'Other') return r.gateway
  if (r.acceptedBy) return `their mail server (${r.acceptedBy})`
  return 'their mail server'
}

export function deliveryVerdict(r: VerdictInput): Verdict {
  const answered = r.rsvp != null && ANSWERED.has(r.rsvp)
  const rsvpNote = answered ? ` (RSVP'd ${r.rsvp} ${fmtDay(r.rsvpAt)} regardless.)` : ''

  // 1. Their server refused it — the strongest fact about THIS message.
  if (r.failedAt) {
    const reason = r.failReason?.trim() || 'no reason recorded'
    return { key: 'failed', tone: 'alert', text: `Rejected by their mail server: ${reason}${rsvpNote}` }
  }
  // 1b. Our own tenant kept it — as terminal as a refusal, and never "in
  //     transit": Client Relations must not wait on a quarantined message.
  if (isHeld(r)) {
    const reason = r.failReason?.trim() || `Exchange Online status: ${r.traceStatus}`
    return { key: 'held', tone: 'alert', text: `Held by Exchange Online: ${reason}${rsvpNote}` }
  }
  // 2. The person answered.
  if (answered) {
    // A hand-entered answer (email/phone, recorded by staff) says nothing
    // about whether the link worked — name it so nobody reads it as a click.
    const how = r.rsvpSource === 'manual' ? ' (entered by hand — answered by email or phone)' : ''
    return { key: 'rsvp', tone: 'ok', text: `RSVP ${r.rsvp} ${fmtDay(r.rsvpAt)}${how}` }
  }
  // 3. Human engagement (invite-level stamps).
  if (r.firstHumanVisitAt) {
    return { key: 'visited', tone: 'ok', text: 'Reached the portal; no RSVP yet.' }
  }
  if (r.firstHumanClickAt) {
    return {
      key: 'opened_no_visit', tone: 'warn',
      text: 'Opened the link but the portal never loaded — device/browser issue, not mail.',
    }
  }
  // 4. Only a machine touched the link.
  if (r.firstMachineClickAt) {
    const where = r.gateway
      ? `Reached their security gateway (${r.gateway})`
      : r.acceptedAt
        ? `Reached ${acceptedByLabel(r)}`
        : 'Reached their mail system'
    return {
      key: 'scanned_only', tone: 'warn',
      text: `${where} — their scanner fetched the link; no person has opened it. Either unopened, in Junk, or blocked at click time.`,
    }
  }
  // 5. Accepted, then silence.
  if (r.acceptedAt) {
    return {
      key: 'accepted_silent', tone: 'neutral',
      text: `Accepted by ${acceptedByLabel(r)}; no scanner or person has touched the link. Either unopened or filtered after acceptance.`,
    }
  }
  // 6. Traced, but no verdict yet.
  if (r.traceStatus != null) {
    return { key: 'in_transit', tone: 'info', text: `In transit — trace shows "${r.traceStatus}", no verdict yet.` }
  }
  // 7. Nothing at all.
  if (r.sentAt < TRACING_SINCE) {
    return { key: 'pre_tracing', tone: 'neutral', text: 'Sent before tracing existed' }
  }
  return { key: 'not_traced', tone: 'neutral', text: 'Not traced yet' }
}

/* ---- funnel predicates (one definition, used by the steps and the table) ---- */

export type FunnelStep = 'sent' | 'accepted' | 'failed' | 'scanned' | 'opened' | 'visited' | 'rsvp' | 'untraced'

export const FUNNEL_MATCH: Record<FunnelStep, (r: DeliveryRow) => boolean> = {
  sent: () => true,
  accepted: (r) => r.acceptedAt != null,
  // Refused OR held — the same rule the API's countSends applies.
  failed: (r) => r.failedAt != null || isHeld(r),
  scanned: (r) => r.firstMachineClickAt != null,
  opened: (r) => r.firstHumanClickAt != null,
  visited: (r) => r.firstHumanVisitAt != null,
  rsvp: (r) => r.rsvp != null && ANSWERED.has(r.rsvp),
  untraced: (r) => r.traceStatus == null,
}

/** Per-send steps count sends; per-person steps (opened / visited / RSVP)
 *  count distinct invites — the same rule the API's countSends applies. */
export function funnelCounts(rows: DeliveryRow[]): Record<FunnelStep, number> {
  const people = (step: FunnelStep) =>
    new Set(rows.filter(FUNNEL_MATCH[step]).map((r) => r.inviteId ?? r.sendId)).size
  const sends = (step: FunnelStep) => rows.filter(FUNNEL_MATCH[step]).length
  return {
    sent: sends('sent'), accepted: sends('accepted'), failed: sends('failed'),
    scanned: sends('scanned'), untraced: sends('untraced'),
    opened: people('opened'), visited: people('visited'), rsvp: people('rsvp'),
  }
}

/* ---- time helpers (local for the eye, UTC for the tooltip) ---- */

export function fmtLocal(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

export function fmtUtc(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return `${d.toISOString().slice(0, 19).replace('T', ' ')} UTC`
}

/** "+4s" / "+2m 10s" / "+1h 03m" between two instants; '' when either is missing. */
export function fmtElapsed(fromIso: string | null | undefined, toIso: string | null | undefined): string {
  if (!fromIso || !toIso) return ''
  const ms = new Date(toIso).getTime() - new Date(fromIso).getTime()
  if (Number.isNaN(ms)) return ''
  const sign = ms < 0 ? '-' : '+'
  const s = Math.round(Math.abs(ms) / 1000)
  if (s < 60) return `${sign}${s}s`
  const m = Math.floor(s / 60)
  if (m < 60) return `${sign}${m}m ${String(s % 60).padStart(2, '0')}s`
  const h = Math.floor(m / 60)
  if (h < 48) return `${sign}${h}h ${String(m % 60).padStart(2, '0')}m`
  return `${sign}${Math.floor(h / 24)}d ${h % 24}h`
}
